import { IConnectRender } from "../../../definitions/IConnectRender";
import { serviceProvider } from "../../../../comun/provider/ServiceProvider";
import { proyectoClienteGrpcService } from "../../../../modulos/proyectos/services/ProyectoClienteGrpcService";
import {
  MiProyectoDetalleResponse,
  MiProyectoItem,
} from "../../../../generated/grpc/proyectos_pb";

const ESTADO_LABEL: Record<string, string> = {
  pendiente: "Pendiente",
  en_ejecucion: "En ejecución",
  completado: "Completado",
};

const SEMAFORO_TEXTO: Record<string, { titulo: string; sub: string }> = {
  verde: {
    titulo: "Todo en orden",
    sub: "El gasto del proyecto va acorde a lo presupuestado.",
  },
  amarillo: {
    titulo: "Atención — desviación moderada",
    sub: "El costo real está superando el presupuesto en algunos ítems. Tu técnico está al tanto.",
  },
  rojo: {
    titulo: "Desviación alta",
    sub: "Varios ítems superan bastante lo presupuestado. Conviene revisarlo con tu técnico.",
  },
};

/**
 * Vista del agricultor sobre sus proyectos (mockup PM_C_Agricultor): selector, cabecera de la
 * finca, estado financiero y listado de labores. **Solo lectura** — no hay ninguna acción que
 * modifique el proyecto.
 */
export class MiProyectoRender implements IConnectRender {
  private _proyectos: MiProyectoItem[] = [];
  private _detalle: MiProyectoDetalleResponse | null = null;
  private _seleccionadoId: string | null = null;
  private _root: HTMLElement | null = null;
  private _onSelectorChange = (): void => {
    const select = this._root?.querySelector<HTMLSelectElement>("#pr-selector");
    if (!select) return;
    this._seleccionadoId = select.value;
    void this._cargarDetalle();
  };

  render(): string {
    return `<div class="pr-wrap" id="pr-wrap"><div class="pr-vacio">Cargando tus proyectos…</div></div>`;
  }

  connect(): void {
    this._root = document.getElementById("pr-wrap");
    void this._cargarLista();
  }

  dispose(): void {
    this._root
      ?.querySelector<HTMLSelectElement>("#pr-selector")
      ?.removeEventListener("change", this._onSelectorChange);
    this._root = null;
    this._detalle = null;
    this._proyectos = [];
  }

  //

  private async _cargarLista(): Promise<void> {
    try {
      this._proyectos = await proyectoClienteGrpcService.getMisProyectosAsync();
    } catch (error) {
      serviceProvider.getErrorService().addError("Proyectos", (error as Error).message);
      this._pintarVacio("No se pudieron cargar tus proyectos.");
      return;
    }

    if (this._proyectos.length === 0) {
      this._pintarVacio("Todavía no tienes proyectos registrados.");
      return;
    }

    this._seleccionadoId = this._proyectos[0].id;
    await this._cargarDetalle();
  }

  private async _cargarDetalle(): Promise<void> {
    if (!this._seleccionadoId) return;
    try {
      this._detalle = await proyectoClienteGrpcService.getDetalleAsync(this._seleccionadoId);
    } catch (error) {
      serviceProvider.getErrorService().addError("Proyectos", (error as Error).message);
      this._pintarVacio("No se pudo cargar el detalle del proyecto.");
      return;
    }
    this._pintar();
  }

  private _pintarVacio(mensaje: string): void {
    if (this._root) this._root.innerHTML = `<div class="pr-vacio">${esc(mensaje)}</div>`;
  }

  private _pintar(): void {
    const d = this._detalle;
    if (!this._root || !d?.proyecto) return;

    const p = d.proyecto;
    const total = Number(p.presupuestoTotal);
    const ejecutado = Number(p.ejecutado);
    const pct = total > 0 ? Math.round((ejecutado / total) * 100) : 0;
    const semaforo = SEMAFORO_TEXTO[d.semaforo] ?? SEMAFORO_TEXTO.verde;
    const barraCls = d.semaforo === "rojo" ? "rojo" : d.semaforo === "amarillo" ? "amarillo" : "";

    this._root.innerHTML = `
      ${this._selectorHtml()}

      <div class="pr-hero">
        <div class="pr-hero-cultivo">${esc(p.cultivoNombre)}${p.variedad ? ` ${esc(p.variedad)}` : ""}</div>
        <div class="pr-hero-nombre">${esc(p.fincaNombre || p.nombre)}</div>
        <div class="pr-hero-finca">${esc(p.municipio)}${p.municipio ? " · " : ""}${fmtHa(p.ha)} Ha</div>
        <div class="pr-hero-meta">
          <div><div class="pr-meta-label">Inicio</div><div class="pr-meta-val">${fmtFecha(p.fechaInicio)}</div></div>
          <div><div class="pr-meta-label">Labores activas</div><div class="pr-meta-val">${p.itemsActivos}</div></div>
          <div><div class="pr-meta-label">Estado</div><div class="pr-meta-val">${esc(estadoProyecto(p.estado))}</div></div>
        </div>
      </div>

      <div class="pr-card">
        <div class="pr-card-title">Estado financiero del proyecto</div>

        <div class="pr-semaforo ${esc(d.semaforo)}">
          <div class="pr-semaforo-dot"></div>
          <div>
            <div class="pr-semaforo-titulo">${esc(semaforo.titulo)}</div>
            <div class="pr-semaforo-sub">${esc(semaforo.sub)}</div>
          </div>
        </div>

        <div class="pr-prog-header">
          <span>Presupuesto ejecutado</span><span class="pr-prog-pct">${pct}%</span>
        </div>
        <div class="pr-bar"><div class="pr-bar-fill ${barraCls}" style="width:${Math.min(pct, 100)}%"></div></div>
        <div class="pr-prog-montos">
          <span>Ejecutado: <strong>${fmtCop(ejecutado)}</strong></span>
          <span>Presupuesto total: <strong>${fmtCop(total)}</strong></span>
        </div>

        ${this._desgloseHtml()}
      </div>

      <div class="pr-card">
        <div class="pr-card-title">Labores del proyecto</div>
        ${this._laboresHtml()}
      </div>
    `;

    this._root
      .querySelector<HTMLSelectElement>("#pr-selector")
      ?.addEventListener("change", this._onSelectorChange);
  }

  private _selectorHtml(): string {
    if (this._proyectos.length <= 1) return "";
    const opciones = this._proyectos
      .map((p) => {
        const etiqueta = `${p.fincaNombre || p.nombre} — ${p.cultivoNombre}${p.municipio ? ` · ${p.municipio}` : ""}`;
        return `<option value="${esc(p.id)}" ${p.id === this._seleccionadoId ? "selected" : ""}>${esc(etiqueta)}</option>`;
      })
      .join("");
    return `
      <div class="pr-selector">
        <label for="pr-selector">Proyecto activo:</label>
        <select class="sx-input" id="pr-selector">${opciones}</select>
      </div>`;
  }

  private _desgloseHtml(): string {
    const d = this._detalle;
    if (!d || d.desglose.length === 0) return "";
    const titulo: Record<string, { badge: string; nombre: string }> = {
      CAPEX: { badge: "Establecimiento", nombre: "Inversión inicial del cultivo" },
      OPEX: { badge: "Sostenimiento", nombre: "Labores e insumos recurrentes" },
    };
    const cards = ["CAPEX", "OPEX"]
      .map((tipo) => {
        const item = d.desglose.find((x) => x.tipo === tipo);
        if (!item) return "";
        const presupuestado = Number(item.presupuestado);
        const ejecutado = Number(item.ejecutado);
        const pct = presupuestado > 0 ? Math.round((ejecutado / presupuestado) * 100) : 0;
        const t = titulo[tipo];
        return `
          <div class="pr-desglose-card">
            <span class="pr-tipo-badge ${tipo.toLowerCase()}">${esc(t.badge)}</span>
            <div class="pr-desglose-nombre">${esc(t.nombre)}</div>
            <div class="pr-desglose-monto">${fmtCop(ejecutado)}</div>
            <div class="pr-desglose-nombre">de ${fmtCop(presupuestado)} · ${pct}%</div>
            <div class="pr-bar" style="margin-top:6px"><div class="pr-bar-fill" style="width:${Math.min(pct, 100)}%"></div></div>
          </div>`;
      })
      .join("");
    return `<div class="pr-desglose">${cards}</div>`;
  }

  private _laboresHtml(): string {
    const d = this._detalle;
    if (!d || d.labores.length === 0) {
      return `<div class="pr-vacio">Este proyecto todavía no tiene labores configuradas.</div>`;
    }

    const secciones = [
      { tipo: "CAPEX", titulo: "Establecimiento" },
      { tipo: "OPEX", titulo: "Sostenimiento" },
    ];

    return secciones
      .map(({ tipo, titulo }) => {
        const delTipo = d.labores.filter((l) => l.tipo === tipo);
        if (delTipo.length === 0) return "";
        const subs = [...new Set(delTipo.map((l) => l.sub))];
        const cuerpo = subs
          .map((sub) => {
            const filas = delTipo
              .filter((l) => l.sub === sub)
              .map(
                (l) => `
                  <div class="pr-labor">
                    <span>${esc(l.nombre)}</span>
                    <span class="pr-estado ${esc(l.estado)}">
                      <span class="pr-estado-dot"></span>${esc(ESTADO_LABEL[l.estado] ?? l.estado)}
                    </span>
                  </div>`,
              )
              .join("");
            return `<div class="pr-sub-label">${esc(sub)}</div>${filas}`;
          })
          .join("");
        return `
          <div class="pr-sec-header">
            <span class="pr-tipo-badge ${tipo.toLowerCase()}">${esc(titulo)}</span>
            <span>${delTipo.length} labores</span>
          </div>
          ${cuerpo}`;
      })
      .join("");
  }
}

//

function esc(valor: string): string {
  return (valor ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Los montos del backend son COP enteros (no centavos). */
function fmtCop(valor: number): string {
  return "$" + new Intl.NumberFormat("es-CO").format(Math.round(valor));
}

function fmtHa(valor: number): string {
  return new Intl.NumberFormat("es-CO", { maximumFractionDigits: 2 }).format(valor);
}

function fmtFecha(iso: string): string {
  if (!iso) return "—";
  const ms = Date.parse(iso);
  if (isNaN(ms)) return "—";
  return new Date(ms).toLocaleDateString("es-CO", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

function estadoProyecto(estado: string): string {
  const labels: Record<string, string> = {
    borrador: "Borrador",
    en_revision: "En revisión",
    aprobado: "Aprobado",
    activo: "En ejecución",
    cerrado: "Cerrado",
    suspendido: "Suspendido",
  };
  return labels[estado] ?? estado;
}
